import { Controller } from '@hotwired/stimulus'
import StimulusReflex from 'stimulus_reflex'

// This is the Stimulus ApplicationController.
// All StimulusReflex controllers should inherit from this class.
//
// Example:
//
//   import ApplicationController from './application_controller'
//
//   export default class extends ApplicationController { ... }
//
// Learn more at: https://docs.stimulusreflex.com
//

export default class extends Controller {
  connect () {
    StimulusReflex.register(this)
    
    // Listen for conversation errors
    this.boundConversationErrorHandler = this.handleConversationError.bind(this)
    document.addEventListener('conversationError', this.boundConversationErrorHandler)
    
    // Also listen for legacy conversationAccessError for backward compatibility
    this.boundConversationAccessErrorHandler = this.handleConversationError.bind(this)
    document.addEventListener('conversationAccessError', this.boundConversationAccessErrorHandler)
  }

  disconnect () {
    if (this.boundConversationErrorHandler) {
      document.removeEventListener('conversationError', this.boundConversationErrorHandler)
    }
    if (this.boundConversationAccessErrorHandler) {
      document.removeEventListener('conversationAccessError', this.boundConversationAccessErrorHandler)
    }
  }

  // Application-wide lifecycle methods
  //
  // Use these methods to handle lifecycle callbacks for all controllers.
  // Using lifecycle methods is optional, so feel free to delete these if you don't need them.
  //
  // Arguments:
  //
  //   element - the element that triggered the reflex
  //             may be different than the Stimulus controller's this.element
  //
  //   reflex - the name of the reflex e.g. "Example#demo"
  //
  //   error/noop - the error message (for reflexError), otherwise null
  //
  //   id - a UUID4 or developer-provided unique identifier for each Reflex
  //

  dispatchCStatusEvent(message) {
    this.dispatchEvent("cStatus", { conversationId: this.element.dataset.id, message })
  }

  dispatchEvent(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail }))
  }

  beforeReflex (element, reflex, noop, id) {
    // document.body.classList.add('wait')
  }

  reflexQueued (element, reflex, noop, id) {
    // Reflex will be delivered to server upon reconnection
  }

  reflexDelivered (element, reflex, noop, id) {
    // Reflex has been delivered to the server
  }

  reflexSuccess (element, reflex, noop, id) {
    // show success message
  }

  reflexError (element, reflex, error, id) {
    // show error message
  }

  reflexForbidden (element, reflex, noop, id) {
    // Reflex action did not have permission to run
    // window.location = '/'
  }

  reflexHalted (element, reflex, noop, id) {
    // handle aborted Reflex action
  }

  afterReflex (element, reflex, noop, id) {
    // document.body.classList.remove('wait')
  }

  finalizeReflex (element, reflex, noop, id) {
    // all operations have completed, animation etc is now safe
  }

  handleConversationError (event) {
    const { message, errorType, showContactSupport, supportUrl } = event.detail
    
    // Create error notification
    this.showConversationError(message, errorType, showContactSupport, supportUrl)
    
    // Always disable chat functionality to prevent further errors
    this.disableChatFunctionality()
  }

  showConversationError (message, errorType, showContactSupport, supportUrl) {
    // Log error type for debugging
    console.log('Conversation error:', errorType, message)
    
    // Remove any existing error notifications
    const existingError = document.querySelector('.conversation-error')
    if (existingError) {
      existingError.remove()
    }

    // Create error notification element
    const errorDiv = document.createElement('div')
    errorDiv.className = 'conversation-error fixed top-4 right-4 bg-red-50 border border-red-200 rounded-lg p-4 shadow-lg z-50 max-w-md'
    
    let html = `
      <div class="flex items-start">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-3 flex-1">
          <h3 class="text-sm font-medium text-red-800">Conversation Error</h3>
          <div class="mt-1 text-sm text-red-700">${message}</div>
          <div class="mt-3 flex space-x-2">
    `
    
    if (showContactSupport && supportUrl) {
      html += `
            <a href="${supportUrl}" target="_blank" class="bg-red-100 text-red-800 px-3 py-1 rounded text-sm font-medium hover:bg-red-200 transition-colors">
              Contact Support
            </a>
      `
    }
    
    html += `
            <button onclick="this.closest('.conversation-error').remove()" class="bg-gray-100 text-gray-700 px-3 py-1 rounded text-sm font-medium hover:bg-gray-200 transition-colors">
              Dismiss
            </button>
          </div>
        </div>
      </div>
    `
    
    errorDiv.innerHTML = html
    document.body.appendChild(errorDiv)
    
    // Auto-dismiss after 10 seconds
    setTimeout(() => {
      if (errorDiv.parentNode) {
        errorDiv.remove()
      }
    }, 10000)
  }

  disableChatFunctionality () {
    // Disable text areas and chat inputs to prevent further errors
    const textareas = document.querySelectorAll('[data-controller*="prompter"] textarea, [data-controller*="prompter"] [contenteditable]')
    textareas.forEach(textarea => {
      textarea.disabled = true
      textarea.contentEditable = false
      textarea.style.opacity = '0.5'
      textarea.placeholder = 'Conversation unavailable'
    })
    
    // Disable reflex buttons
    const reflexButtons = document.querySelectorAll('[data-reflex^="ConversationReflex"]')
    reflexButtons.forEach(button => {
      button.disabled = true
      button.style.opacity = '0.5'
    })
  }
}
